<template>
  <div class="container">
    <div class="card theme-card border-lg-0">
      <div class="card-body px-2 px-sm-4">
        <div class="row">
          <div
            v-if="!isPatient"
            class="
              col-12 col-sm-6 col-xl-4
              mb-3
              d-flex
              align-items-md-center
              flex-md-row flex-column
            "
          >
            <label class="col-4 col-sm-auto col-form-label mb-md-0 mb-2">
              Service:
            </label>
            <multiselect
              class="app-select"
              v-model="selectedService"
              :options="dropdownServices"
              :loading="activeServicesLoading"
              placeholder="Search for Service"
              value="value"
              label="label"
              track-by="value"
              :required="true"
            >
            </multiselect>
          </div>
          <div
            v-if="!isPatient"
            class="
              col-12 col-sm-6 col-xl-4
              mb-3
              d-flex
              align-items-md-center
              flex-md-row flex-column
            "
          >
            <label class="col-auto col-form-label mb-md-0 mb-2">
              Treatment:
            </label>
            <multiselect
              class="app-select"
              v-model="selectedTreatment"
              :options="dropdownTreatments"
              placeholder="Search for Treatment"
              Value="value"
              label="label"
              track-by="value"
              :loading="basicTreatmentsLoading"
            >
            </multiselect>
          </div>
          <div
            class="
              col-12 col-sm-6 col-xl-4
              mb-3
              d-flex
              align-items-md-center
              flex-md-row flex-column
            "
          >
            <label class="col-auto col-form-label mb-md-0 mb-2">
              Booking Status:
            </label>
            <select v-model="bookingStatus" class="form-control">
              <option
                v-for="option in bookingStatusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <div
            class="
              col-12 col-sm-6 col-xl-4
              mb-3
              d-flex
              align-items-md-center
              flex-md-row flex-column
            "
          >
            <label class="col-auto col-form-label mb-md-0 mb-2">
              Payment Status:
            </label>
            <select v-model="paymentStatus" class="form-control">
              <option
                v-for="option in paymentStatusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        <div v-if="!isPatient">
          <div class="form-group mb-0 invoice-number-input">
            <span class="input-prefix"> INV- </span>
            <input
              type="number"
              class="form-control pr-5"
              @keydown.enter="searchByInvoiceNumberHandler"
              v-model="invoiceNumber"
              placeholder="Search by Invoice number"
            />
            <span>
              <i
                class="fa fa-search search-icon link-black"
                @click="searchByInvoiceNumberHandler"
              ></i>
              <i
                v-if="invoiceNumber"
                class="fas fa-times cancel-icon"
                @click="(invoiceNumber = ''), searchByInvoiceNumberHandler()"
              ></i>
            </span>
          </div>
          <small class="text-muted pl-2">
            All other filters will be ignored when searching by invoice number.
          </small>
        </div>
      </div>
      <div class="d-flex justify-content-end flex-wrap">
        <div class="d-flex align-items-center mr-auto text-dark col-auto mb-3">
          <span>
            <b>
              <i class="fas fa-spin fa-circle-notch" v-if="busy"></i>
              {{ busy ? "" : bookingRequests.length }}/{{ total }}
            </b>
            Booking Request{{ bookingRequestsLoading || total != 1 ? "s" : "" }}
          </span>
        </div>
      </div>
      <alert
        v-if="bookingRequestsLoading || onlineBookingSettingsLoading"
        class="light-shadow mx-4 mb-4"
      />
      <alert
        class="mx-4 mb-4"
        :hideLoader="true"
        v-else-if="bookingRequests.length === 0"
      >
        No booking requests found.
      </alert>
      <div
        class="
          card
          mb-0
          theme-card
          mobile-tablet-no-card
          p-2 p-md-0
          table-container
          border-lg-0
        "
        v-else
      >
        <table class="table table-striped mb-0 responsive-mobile-tablet-table">
          <thead>
            <tr>
              <th>Client</th>
              <th v-if="!isPatient">Service</th>
              <th>Treatment</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Additional Notes</th>
              <th>Referred By</th>
              <th>Status</th>
              <th>Payment method</th>
              <th>Payment Status</th>
              <th>
                Action
                <span
                  v-tooltip.top="
                    'The pay button only appears for accepted bookings with a payment method of Card or Payment Link and an appointment status of Verified.'
                  "
                >
                  <icon type="info-circle fa-lg" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="bookingRequest in bookingRequests"
              :key="bookingRequest.id"
            >
              <td>
                <b class="mobile-tablet-only mr-2">Name:</b>
                {{ bookingRequest.user.name }}
              </td>
              <td v-if="!isPatient">
                <b class="mobile-tablet-only mr-2">Service:</b>
                {{ bookingRequest.treatment.service.name }}
              </td>
              <td>
                <b class="mobile-tablet-only mr-2">Treatment:</b>
                {{ bookingRequest.treatment.name }}
              </td>
              <td>
                <b class="mobile-tablet-only mr-2">Start Time:</b>
                {{ dateFormat(bookingRequest.start_time) }}
              </td>
              <td>
                <b class="mobile-tablet-only mr-2">End Time:</b>
                {{ dateFormat(bookingRequest.end_time) }}
              </td>
              <td>
                <b class="mobile-tablet-only mr-2">Additional Notes:</b>
                <button
                  class="btn btn-theme"
                  @click="showNotes(bookingRequest)"
                >
                  <icon type="eye fa-lg" />
                </button>
              </td>
              <td>
                <b class="mobile-tablet-only mr-2">Referred By</b>
                {{
                  bookingRequest.referred_by_type
                    ? `${textToUpperCase(bookingRequest.referred_by_type)} ${
                        bookingRequest.referred_by_name
                          ? `/ ${bookingRequest.referred_by_name}`
                          : ""
                      }`
                    : "N/A"
                }}
              </td>
              <td>
                <b class="mobile-tablet-only mr-2">Status:</b>
                <span
                  class="font-weight-bold p-2"
                  :class="
                    {
                      pending: 'badge badge-pill badge-warning',
                      accepted: 'badge badge-pill badge-deep-success',
                      rejected: 'badge badge-pill badge-danger',
                      null: 'badge badge-pill badge-secondary',
                    }[bookingRequest.status]
                  "
                >
                  {{ textToUpperCase(bookingRequest.status) }}
                </span>
              </td>
              <td>
                <b class="mobile-tablet-only mr-2">Payment Method:</b>
                <span class="font-weight-bold p-2">
                  {{ getPaymentMethod(bookingRequest) || "N/A" }}
                </span>
              </td>
              <td>
                <b class="mobile-tablet-only mr-2">Payment Status:</b>
                <span
                  class="font-weight-bold p-2"
                  :class="
                    {
                      unpaid: 'badge badge-pill badge-warning',
                      paid: 'badge badge-pill badge-deep-success',
                      failed: 'badge badge-pill badge-danger',
                      null: 'badge badge-pill badge-secondary',
                    }[bookingRequest.payment_status]
                  "
                >
                  {{ textToUpperCase(bookingRequest.payment_status) }}
                </span>
              </td>
              <td class="td-actions">
                <div
                  class="d-flex justify-content-center justify-content-xl-start"
                >
                  <button
                    v-if="showActionsButton(bookingRequest)"
                    class="btn btn-black ml-1 mr-2"
                    title="Accept Booking Request"
                    @click="acceptBookingRequestHelper(bookingRequest)"
                  >
                    <icon type="calendar-check fa-lg" />
                  </button>
                  <button
                    v-if="showActionsButton(bookingRequest)"
                    class="btn btn-danger ml-1 mr-2"
                    title="Reject Booking Request"
                    @click="rejectBookingRequestHelper(bookingRequest)"
                  >
                    <icon type="calendar-xmark fa-lg" />
                  </button>
                  <a
                    v-if="!isPatient"
                    v-tooltip="'Create New Intake Form'"
                    class="ml-2"
                    :href="`/clients/${bookingRequest.user.profile_id}/intake-forms/create`"
                    target="_blank"
                  >
                    <img
                      class="tools-img light-shadow rounded-circle border"
                      src="@/assets/img/firstHx-logo1.png"
                      width="43"
                      alt="Create New Intake Form"
                    />
                  </a>
                  <button
                    v-if="showPayButton(bookingRequest)"
                    v-tooltip="getPayButtonTitle(bookingRequest)"
                    class="btn btn-deep-success ml-1 mr-2"
                    :title="getPayButtonTitle(bookingRequest)"
                    @click="sendPaymentEventRequest(bookingRequest)"
                  >
                    <i
                      class="fas fa-spin fa-circle-notch"
                      v-if="payAppointmentLoading == bookingRequest.id"
                    ></i>
                    <icon v-else type="credit-card fa-lg" />
                  </button>
                  <button
                    v-if="showMarkAsPaidButton(bookingRequest)"
                    v-tooltip="'Mark as Paid'"
                    class="btn btn-deep-success ml-1 mr-2"
                    title="Mark as Paid"
                    @click="markAsPaidHandler(bookingRequest)"
                  >
                    <i
                      class="fas fa-spin fa-circle-notch"
                      v-if="markAsPaidLoading == bookingRequest.appointment_id"
                    ></i>
                    <icon v-else type="check-circle fa-lg" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <mugen-scroll
          :handler="loadMore"
          :should-handle="
            !busy && !bookingRequestsLoading && bookingRequests.length < total
          "
          scroll-container="wrap"
        >
          <alert
            v-if="busy"
            class="
              text-center
              p-2
              mb-0
              mobile-tablet-loadmore mobile-tablet-shadow-card
            "
          >
            <i class="fas fa-spin fa-circle-notch mr-2"></i>
            Loading more requests...
          </alert>
        </mugen-scroll>
      </div>
    </div>
    <accept-booking-request-modal
      v-if="triggeredBookingRequest"
      :booking-request="triggeredBookingRequest"
    />
    <reject-booking-request-modal
      v-if="triggeredBookingRequest"
      :booking-request="triggeredBookingRequest"
    />
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import Multiselect from "vue-multiselect";
import dayjs from "dayjs";
import helpers from "@/utils/helpers";
import MugenScroll from "vue-mugen-scroll";
import AcceptBookingRequestModal from "./AcceptBookingRequestModal.vue";
import RejectBookingRequestModal from "./RejectBookingRequestModal.vue";
import Swal from "sweetalert2";

export default {
  components: {
    Multiselect,
    MugenScroll,
    AcceptBookingRequestModal,
    RejectBookingRequestModal,
  },
  data() {
    return {
      busy: false,
      limit: 50,
      selectedTreatment: null,
      selectedService: null,
      bookingStatus: "pending",
      payAppointmentLoading: null,
      bookingStatusOptions: [
        { label: "All", value: null },
        { label: "Pending", value: "pending" },
        { label: "Accepted", value: "accepted" },
        { label: "Rejected", value: "rejected" },
      ],
      paymentStatus: null,
      paymentStatusOptions: [
        { label: "All", value: null },
        { label: "Unpaid", value: "unpaid" },
        { label: "Paid", value: "paid" },
        { label: "Failed", value: "failed" },
      ],
      triggeredBookingRequest: null,
      invoiceNumber: "",
      latestInvoiceNumber: "",
    };
  },
  mounted() {
    this.getBookingRequestsHelper(1);
    this.getActiveServices();
    this.getBasicTreatments();
  },
  watch: {
    selectedTreatment: function () {
      this.getBookingRequestsHelper(1);
    },
    bookingStatus: function () {
      this.getBookingRequestsHelper(1);
    },
    paymentStatus: function () {
      this.getBookingRequestsHelper(1);
    },
    selectedService: function () {
      this.getBookingRequestsHelper(1);
    },
    isPatient: function (isPatientValue) {
      if (isPatientValue !== undefined && !isPatientValue) {
        this.getOnlineBookingSettings();
      }
    },
  },
  computed: {
    ...mapState({
      isPatient: (state) => state.auth.user?.isPatient,
      isProvider: (state) => state.auth.user?.isProvider,
      bookingRequestsLoading: (state) => state.booking.bookingRequests.loading,
      bookingRequests: (state) => state.booking.bookingRequests.pagination.data,
      total: (state) => state.booking.bookingRequests.pagination.total,
      page: (state) => state.booking.bookingRequests.pagination.page,
      basicTreatments: (state) => state.treatments.basicTreatments.data,
      basicTreatmentsLoading: (state) =>
        state.treatments.basicTreatments.loading,
      activeServices: (state) => state.services.activeServices.data,
      activeServicesLoading: (state) => state.services.activeServices.loading,
      onlineBookingSettingsLoading: (state) =>
        state.settings.onlineBookingSettings.loading,
      onlineBookingSettings: (state) =>
        state.settings.onlineBookingSettings.data,
      markAsPaidLoading: (state) => state.appointments.markAsPaidLoading,
    }),
    dropdownTreatments: function () {
      return this.basicTreatments.map((treatment) => ({
        label: treatment.name,
        value: treatment.id,
      }));
    },
    dropdownServices() {
      return this.activeServices.map((service) => ({
        label: service.name,
        value: service.id,
      }));
    },
  },
  methods: {
    ...mapActions({
      getBookingRequests: "booking/getBookingRequests",
      markBookingRequestAsPaid: "booking/markBookingRequestAsPaid",
      getBasicTreatments: "treatments/getBasicTreatments",
      getActiveServices: "services/getActiveServices",
      getOnlineBookingSettings: "settings/getOnlineBookingSettings",
      markAppointmentAsPaid: "appointments/markAsPaid",
    }),
    dateFormat: function (date) {
      return dayjs(date).utc().format("MMM D, YYYY [at] h:mm a");
    },
    textToUpperCase: function (status) {
      return helpers.textToUpperCase(status);
    },
    getPaymentMethod: function (bookingRequest) {
      switch (bookingRequest.payment_method) {
        case "credit_card":
          return "Card";
        case "secure_payment_link":
          return "Payment Link";
        case "email_money_transfer":
          return "Email Money Transfer";
        case "other":
          return "Other";
        default:
          return "N/A";
      }
    },
    showPayButton: function (bookingRequest) {
      return (
        bookingRequest.payment_status !== "paid" &&
        bookingRequest.status === "accepted" &&
        bookingRequest.payment_method !== "other" &&
        bookingRequest.appointment_status === "Verified"
      );
    },
    showMarkAsPaidButton: function (bookingRequest) {
      if (this.isPatient || this.isProvider) return false;

      if (bookingRequest.payment_method === "other") {
        return (
          bookingRequest.payment_status === "unpaid" &&
          bookingRequest.status === "accepted" &&
          bookingRequest.appointment_status === "Verified"
        );
      }
      return (
        bookingRequest.latest_pay_attempt &&
        bookingRequest.payment_status !== "paid" &&
        bookingRequest.status === "accepted" &&
        bookingRequest.appointment_status === "Verified"
      );
    },
    getBookingRequestsHelper: function (page) {
      const payload = {
        limit: this.limit,
        page: page || this.page,
      };
      if (this.selectedTreatment) {
        payload.treatmentId = this.selectedTreatment.value;
      }
      if (this.selectedService) {
        payload.serviceId = this.selectedService.value;
      }
      if (this.bookingStatus) {
        payload.status = this.bookingStatus;
      }
      if (this.paymentStatus) {
        payload.paymentStatus = this.paymentStatus;
      }
      if (this.invoiceNumber) {
        payload.invoiceNumber = this.invoiceNumber;
      }

      this.getBookingRequests(payload).then(() => {
        this.busy = false;
      });
    },
    loadMore: function () {
      if (this.bookingRequests.length < this.total) {
        this.busy = true;
        this.getBookingRequestsHelper(this.page + 1);
      }
    },
    acceptBookingRequestHelper: function (bookingRequest) {
      this.triggeredBookingRequest = bookingRequest;
      this.$nextTick(() => {
        this.$modal.show("acceptBookingRequestModal");
      });
    },
    rejectBookingRequestHelper: function (bookingRequest) {
      this.triggeredBookingRequest = bookingRequest;
      this.$nextTick(() => {
        this.$modal.show("rejectBookingRequestModal");
      });
    },
    sendPaymentEventRequest: async function (bookingRequest) {
      const vm = this;
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-black ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });
      let confirmButtonText = bookingRequest.latest_pay_attempt
        ? "Try pay again!"
        : "Yes, pay now!";
      if (bookingRequest.payment_method === "secure_payment_link") {
        confirmButtonText = bookingRequest.latest_pay_attempt
          ? "Resend payment link!"
          : "Yes, send payment link!";
      }
      if (bookingRequest.payment_method === "email_money_transfer") {
        confirmButtonText = bookingRequest.latest_pay_attempt
          ? "Resend money transfer email!"
          : "Yes, send money transfer email!";
      }

      return swalWithBootstrapButtons
        .fire({
          title: "Payment Details",
          html: `<div class="text-center">
                    ${
                      bookingRequest.latest_pay_attempt
                        ? `<p class="text-muted">Latest payment attempt: ${helpers.prettyDate(
                            bookingRequest.latest_pay_attempt
                          )}</p>`
                        : ""
                    }
                    <div class="d-flex justify-content-between mx-3">
                        <span><b>Amount:</b></span>
                        <span>$${`${+parseFloat(
                          bookingRequest.cost?.amount || 0
                        ).toFixed(2)}`.replace(
                          /(\d)(?=(\d{3})+(?!\d))/g,
                          "$1,"
                        )}</span>
                    </div>
                    <div class="d-flex justify-content-between mx-3">
                        <span><b>Expenses:</b></span>
                        <span>$${`${+parseFloat(
                          bookingRequest.cost?.expenses || 0
                        ).toFixed(2)}`.replace(
                          /(\d)(?=(\d{3})+(?!\d))/g,
                          "$1,"
                        )}</span>
                    </div>
                     <div class="d-flex justify-content-between mx-3">
                        <span><b>Sub Total <small class="text-muted">(Inc. expenses)</small>:</b></span>
                          <span>$${`${+parseFloat(
                            bookingRequest.cost?.sub_total || 0
                          ).toFixed(2)}`.replace(
                            /(\d)(?=(\d{3})+(?!\d))/g,
                            "$1,"
                          )}</span>
                    </div>
                    <div class="d-flex justify-content-between mx-3">
                        <span><b>Interest:</b></span>
                        <span>$${`${+parseFloat(
                          bookingRequest.cost?.interest || 0
                        ).toFixed(2)}`.replace(
                          /(\d)(?=(\d{3})+(?!\d))/g,
                          "$1,"
                        )}</span>
                    </div>
                    <div class="d-flex justify-content-between mx-3">
                        <span><b>Tax:</b></span>
                        <span>$${`${+parseFloat(
                          bookingRequest.cost?.tax || 0
                        ).toFixed(2)}`.replace(
                          /(\d)(?=(\d{3})+(?!\d))/g,
                          "$1,"
                        )}</span>
                    </div>
                    <hr/>
                    <div class="d-flex justify-content-between mt-3 mx-3">
                        <span><b>Total:</b></span>
                          <span>$${`${+parseFloat(
                            bookingRequest.cost?.total || 0
                          ).toFixed(2)}`.replace(
                            /(\d)(?=(\d{3})+(?!\d))/g,
                            "$1,"
                          )}</span>
                    </div>
                   </div>`,
          icon: "",
          showCancelButton: true,
          confirmButtonText,
          cancelButtonText: "Cancel",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            try {
              vm.payAppointmentLoading = bookingRequest.id;
              const res = await vm.$http.post(
                `appointments/${bookingRequest.appointment_id}/pay`
              );
              if (res) {
                vm.getBookingRequestsHelper(vm.page);
                Swal.fire({
                  title: "Success",
                  text: res.data?.message || "Payment successful.",
                  icon: "success",
                });
              }
            } catch (err) {
              Swal.fire("Error", err.data.error.message, "error");
            } finally {
              vm.payAppointmentLoading = null;
            }
          }
        });
    },
    showNotes: function (bookingRequest) {
      Swal.fire({
        html: `<div class="booking-requests-additional-notes">
          <h3>Additional Notes</h3>
          <div class="additional-notes-container d-flex align-items-baseline justify-content-between flex-wrap border rounded p-3 mb-3">
          ${bookingRequest.additional_notes || "No additional notes."}
          </div>
          <hr>
          </div>`,
        showCancelButton: true,
        cancelButtonText: "Close",
        cancelButtonColor: "#6e84a3",
        showCloseButton: true,
        showConfirmButton: false,
        customClass: {
          popup: "px-0 booking-note",
        },
      });
    },
    showActionsButton: function (bookingRequest) {
      if (this.isPatient) return false;
      if (this.isProvider)
        return (
          bookingRequest.status === "pending" &&
          this.onlineBookingSettings?.providers_can_perform_actions
        );
      return bookingRequest.status === "pending";
    },
    searchByInvoiceNumberHandler: function () {
      if (this.latestInvoiceNumber == this.invoiceNumber) {
        return;
      }
      this.latestInvoiceNumber = this.invoiceNumber;
      this.getBookingRequestsHelper(1);
    },
    getPayButtonTitle: function (bookingRequest) {
      if (bookingRequest.payment_method === "secure_payment_link") {
        return "Send Payment Link";
      }
      if (bookingRequest.payment_method === "email_money_transfer") {
        let title = "Send Email Money Transfer";
        if (bookingRequest.latest_pay_attempt) {
          title = `Resend Email Money Transfer \n Latest payment attempt: ${helpers.prettyDate(
            bookingRequest.latest_pay_attempt
          )}`;
        }
        return title;
      }
      return "Pay Appointment";
    },
    markAsPaidHandler: function (bookingRequest) {
      const vm = this;
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-black ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });
      swalWithBootstrapButtons
        .fire({
          title: "Mark as Paid",
          text: "Are you sure you want to mark this booking request as paid?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, mark as paid!",
          cancelButtonText: "Cancel",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            const success = await vm.markAppointmentAsPaid(
              bookingRequest.appointment_id
            );
            if (success) {
              vm.markBookingRequestAsPaid(bookingRequest.id);
              Swal.fire({
                title: "Success",
                text: "Booking request marked as paid successfully.",
                icon: "success",
              });
            }
          }
        });
    },
  },
};
</script>
<style lang="scss" scoped>
.dropdown-icon {
  font-size: 1.5rem;
}

.form-group {
  position: relative;
}

.invoice-number-input {
  overflow: hidden;
  input {
    padding-left: 50px;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .cancel-icon,
  .search-icon {
    z-index: 10;
    position: absolute;
    right: 39px;
    top: 50%;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%);
    color: #dc143c;
    cursor: pointer;
  }
  .search-icon {
    right: 14px;
  }

  .input-prefix {
    position: absolute;
    left: 10px;
    top: 50%;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%);
    color: black;
    opacity: 0.6;
    font-weight: 600;
  }
}
</style>
<style lang="scss">
.booking-requests-additional-notes {
  .additional-notes-container {
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
  }
}
.booking-note {
  hr {
    display: none;
  }
  .swal2-actions {
    margin-top: 0;
  }
}
</style>
